/* Copyright (c) Trimble Inc. All rights reserved. */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useTokenStore } from '../stores/Token.store';
import { JWTParseHelper } from '../helpers/JWTParser.helper';
import AuthRedrectionHelper from '../helpers/AuthRedirector.helper';
import {
  SESSION_EXPIRY_COUNTDOWN_SECONDS,
  SESSION_EXPIRY_WARN_BEFORE_SECONDS,
} from '../config/CONSTANTS';
import { Logger } from '@tcp-base/toolkit';

export interface SessionExpiryMonitorResult {
  isDialogOpen: boolean;
  countdown: number;
  onStaySignedIn: () => void;
  onLogout: () => void;
}

/**
 * Module-level singleton guard.
 * Prevents multiple instances of the monitor running simultaneously
 * (e.g. if AuthProvider is remounted or the hook is accidentally used in multiple places).
 */
let isMonitorActive = false;

/**
 * useSessionExpiryMonitor
 *
 * Watches the stored access token and proactively warns the user before it expires.
 *
 * Behaviour:
 * - When the token has more than SESSION_EXPIRY_WARN_BEFORE_SECONDS left, schedules a
 *   timeout to open the dialog at the correct moment.
 * - When the token has SESSION_EXPIRY_WARN_BEFORE_SECONDS or fewer seconds remaining the
 *   dialog is opened immediately with a countdown capped at SESSION_EXPIRY_COUNTDOWN_SECONDS.
 * - The countdown ticks every second; reaching 0 triggers automatic logout.
 * - Calling onStaySignedIn() initiates re-authentication (existing path-save + redirect logic
 *   in AuthRedrectionHelper.login() handles returning the user to the same page).
 * - Calling onLogout() clears session data and redirects to the login page.
 */
export const useSessionExpiryMonitor = (): SessionExpiryMonitorResult => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [countdown, setCountdown] = useState(SESSION_EXPIRY_COUNTDOWN_SECONDS);

  // Refs for timers so we can clear them on token change or unmount
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (warnTimerRef.current !== null) {
      clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (countdownIntervalRef.current !== null) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const performLogout = useCallback(() => {
    clearAllTimers();
    try {
      Logger.info('useSessionExpiryMonitor: performing logout due to session expiry');
      AuthRedrectionHelper.logout({ clearLocalStorage: true });
    } catch (error) {
      Logger.error('useSessionExpiryMonitor: logout failed:', error);
    }
  }, [clearAllTimers]);

  const openDialog = useCallback(
    (startCountdown: number) => {
      // Clear any existing countdown before starting a new one
      if (countdownIntervalRef.current !== null) {
        clearInterval(countdownIntervalRef.current);
      }

      setCountdown(startCountdown);
      setIsDialogOpen(true);

      // Tick down every second
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Countdown reached zero — auto logout
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
            }
            countdownIntervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [performLogout],
  );

  const scheduleWarning = useCallback(
    (token: string) => {
      clearAllTimers();
      setIsDialogOpen(false);

      // If a silent re-authentication is in progress (user clicked "Stay Signed In"),
      // skip scheduling until the new token arrives — prevents the dialog from
      // flashing on the OAuth callback page.
      if (sessionStorage.getItem('AUTH_REFRESHING') === 'true') {
        Logger.debug(
          'useSessionExpiryMonitor: AUTH_REFRESHING in progress, skipping warning schedule',
        );
        return;
      }

      const expiry = JWTParseHelper.getJWTExpiration(token);
      if (!expiry) {
        // Token has no expiration claim — nothing to monitor
        Logger.debug('useSessionExpiryMonitor: token has no expiry, skipping monitor');
        return;
      }

      const secondsRemaining = Math.floor((expiry.getTime() - Date.now()) / 1000);

      if (secondsRemaining <= 0) {
        // Already expired. Do NOT redirect/logout here: re-authentication is driven by
        // the next API call that returns 401, not by a proactive client-side check.
        Logger.warn('useSessionExpiryMonitor: token already expired, waiting for server 401');
        return;
      }

      if (secondsRemaining <= SESSION_EXPIRY_WARN_BEFORE_SECONDS) {
        // Within the warning window — open dialog now
        const startCountdown = Math.min(secondsRemaining, SESSION_EXPIRY_COUNTDOWN_SECONDS);
        Logger.debug(
          `useSessionExpiryMonitor: within warning window, opening dialog (countdown=${startCountdown}s)`,
        );
        openDialog(startCountdown);
      } else {
        // Schedule the dialog to open at the right moment
        const delayMs = (secondsRemaining - SESSION_EXPIRY_WARN_BEFORE_SECONDS) * 1000;
        Logger.debug(
          `useSessionExpiryMonitor: scheduling warning in ${
            delayMs / 1000
          }s (token expires in ${secondsRemaining}s)`,
        );
        warnTimerRef.current = setTimeout(() => {
          openDialog(SESSION_EXPIRY_COUNTDOWN_SECONDS);
        }, delayMs);
      }
    },
    [clearAllTimers, openDialog, performLogout],
  );

  const onStaySignedIn = useCallback(() => {
    clearAllTimers();
    setIsDialogOpen(false);
    // Mark that we are doing a silent re-auth triggered by the expiry dialog.
    // TokenExchanger will clear this flag once the new token has been saved,
    // which prevents the dialog from flashing on the callback page.
    sessionStorage.setItem('AUTH_REFRESHING', 'true');
    try {
      AuthRedrectionHelper.login().catch((error) => {
        Logger.error('useSessionExpiryMonitor: login redirect failed:', error);
        sessionStorage.removeItem('AUTH_REFRESHING');
      });
    } catch (error) {
      Logger.error('useSessionExpiryMonitor: login failed:', error);
      sessionStorage.removeItem('AUTH_REFRESHING');
    }
  }, [clearAllTimers]);

  const onLogout = useCallback(() => {
    performLogout();
  }, [performLogout]);

  useEffect(() => {
    return () => {
      if (isDialogOpen && countdown === 0) {
        performLogout();
      }
    };
  }, [performLogout]);

  useEffect(() => {
    // Singleton guard — only one monitor may run at a time
    if (isMonitorActive) {
      Logger.warn('useSessionExpiryMonitor: another monitor instance is already active, skipping');
      return;
    }
    isMonitorActive = true;

    // If we landed here as part of a silent re-auth (user clicked "Stay Signed In"),
    // skip the initial schedule — the subscription below will fire once the new
    // token arrives and scheduleWarning() will restart the monitor cleanly.
    if (sessionStorage.getItem('AUTH_REFRESHING') === 'true') {
      Logger.debug(
        'useSessionExpiryMonitor: AUTH_REFRESHING detected on mount, waiting for new token',
      );
    } else {
      // Read the initial token synchronously from the store
      const initialToken = useTokenStore.getState().tokenDetails.access_token;
      if (initialToken) {
        scheduleWarning(initialToken);
      }
    }

    // Subscribe to future token changes (new login, logout, etc.)
    const unsubscribe = useTokenStore.subscribe((state) => {
      const token = state.tokenDetails.access_token;
      if (token) {
        // New token received — clear any in-progress refresh flag so the
        // monitor can resume normal expiry tracking for the fresh token.
        sessionStorage.removeItem('AUTH_REFRESHING');
        scheduleWarning(token);
      } else {
        // Token cleared (logout from another source) — reset the monitor
        clearAllTimers();
        setIsDialogOpen(false);
      }
    });

    return (): void => {
      isMonitorActive = false;
      clearAllTimers();
      unsubscribe();
    };
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional empty dep array: this effect must run exactly once on mount
  }, []);

  return { isDialogOpen, countdown, onStaySignedIn, onLogout };
};

export default useSessionExpiryMonitor;
